import { useEffect } from 'react';
import { useSiteConfig } from '@/hooks/use-site-settings';

/**
 * Component that dynamically updates the document head
 * based on site configuration from the database.
 *
 * Updates: favicon, title, and meta tags.
 */
export function DynamicHead() {
  const { data: siteConfig } = useSiteConfig();

  useEffect(() => {
    if (!siteConfig) return;

    // Update favicon
    if (siteConfig.favicon_url) {
      let faviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement;

      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        document.head.appendChild(faviconLink);
      }

      faviconLink.href = siteConfig.favicon_url;
      faviconLink.type = 'image/png';

      // Also add apple-touch-icon
      let appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement;
      if (!appleTouchIcon) {
        appleTouchIcon = document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        document.head.appendChild(appleTouchIcon);
      }
      appleTouchIcon.href = siteConfig.favicon_url;
    }

    // Update document title (only if SEO title is set)
    if (siteConfig.seo_title) {
      document.title = siteConfig.seo_title;
    }

    // Update meta description
    if (siteConfig.seo_description) {
      let metaDesc = document.querySelector("meta[name='description']") as HTMLMetaElement;
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = siteConfig.seo_description;
    }

    // Update OG title
    if (siteConfig.seo_title) {
      let ogTitle = document.querySelector("meta[property='og:title']") as HTMLMetaElement;
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.content = siteConfig.seo_title;
    }

    // Update OG description
    if (siteConfig.seo_description) {
      let ogDesc = document.querySelector("meta[property='og:description']") as HTMLMetaElement;
      if (!ogDesc) {
        ogDesc = document.createElement('meta');
        ogDesc.setAttribute('property', 'og:description');
        document.head.appendChild(ogDesc);
      }
      ogDesc.content = siteConfig.seo_description;
    }

    // Inject custom scripts from site_config
    if (siteConfig.scripts_header) {
      const existingHeaderScript = document.getElementById('cms-header-scripts');
      if (!existingHeaderScript) {
        const headerScriptContainer = document.createElement('div');
        headerScriptContainer.id = 'cms-header-scripts';
        headerScriptContainer.innerHTML = siteConfig.scripts_header;
        document.head.appendChild(headerScriptContainer);
      }
    }

  }, [siteConfig]);

  // Inject body start scripts
  useEffect(() => {
    if (!siteConfig?.scripts_body_start) return;

    const existingBodyScript = document.getElementById('cms-body-start-scripts');
    if (!existingBodyScript) {
      const bodyScriptContainer = document.createElement('div');
      bodyScriptContainer.id = 'cms-body-start-scripts';
      bodyScriptContainer.innerHTML = siteConfig.scripts_body_start;
      document.body.insertBefore(bodyScriptContainer, document.body.firstChild);
    }
  }, [siteConfig?.scripts_body_start]);

  return null; // This component doesn't render anything
}
